/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Redirect } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { CustomFilterV2, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsNsir from '../../../store/nsir/actions';
import * as Role from '../../../constants/role';
import TableService from '../../../services/TableService';
import ChartsRow from './ChartsRow';
import KpiStrip from './KpiStrip';
import PivotExplorerDialog from './PivotExplorerDialog';
import {
  EMPTY_RESUMO,
  STATUS_FILTER_OPTIONS,
  formatMedicaoPeriodLabel,
  getMedicaoPeriod,
  isMedicaoPeriod,
  normalizeFilterForApi,
  normalizeResumo,
} from './constants';
import {
  renderLocalRede,
  renderMalhaGerencia,
  renderNsirStatus,
  renderTipoServicoSubtipo,
  renderVolumes,
} from './tableCells';

const getDefaultFilter = (authData) => {
  const { inicio, fim } = getMedicaoPeriod();
  return {
    inicio,
    fim,
    dateTypes: ['DATA_DIGITACAO'],
    digitadorId: authData?.role === Role.DIGITADOR ? [authData.id] : [],
  };
};

const formatDate = (value) => {
  if (value == null) return '—';
  const n = Number(value);
  return moment(!Number.isNaN(n) && n > 0 ? n : value).format('DD/MM/YYYY');
};

const formatDateTime = (value) => {
  if (value == null) return '—';
  const n = Number(value);
  return moment(!Number.isNaN(n) && n > 0 ? n : value).format('DD/MM/YYYY HH:mm');
};

const RelatorioDigitacaoV2 = ({
  authData,
  generateReportDigitadorDiario,
}) => {
  const defaultFilter = useMemo(() => getDefaultFilter(authData), [authData]);

  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [activeStatus, setActiveStatus] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openPivot, setOpenPivot] = useState(false);
  const [reload, setReload] = useState(false);
  const [resumo, setResumo] = useState(EMPTY_RESUMO);
  const [resumoLoading, setResumoLoading] = useState(false);

  const isDigitadorAdmin =
    Role.ADMIN === authData?.role || Role.SUPER_ADMIN === authData?.role;

  const apiFilterBase = useMemo(
    () => normalizeFilterForApi(localFilter),
    [localFilter],
  );

  const apiFilter = useMemo(() => {
    if (activeStatus) {
      return normalizeFilterForApi({ ...localFilter, status: activeStatus });
    }
    return apiFilterBase;
  }, [localFilter, activeStatus, apiFilterBase]);

  const periodoLabel = useMemo(() => {
    const range = formatMedicaoPeriodLabel(localFilter.inicio, localFilter.fim);
    if (!range) return null;
    const prefix = isMedicaoPeriod(localFilter.inicio, localFilter.fim)
      ? 'Período de medição'
      : 'Período selecionado';
    return `${prefix}: ${range}`;
  }, [localFilter.inicio, localFilter.fim]);

  const fetchResumo = useCallback(async () => {
    setResumoLoading(true);
    try {
      const response = await TableService.request(
        'reports/digitacao/resumo',
        apiFilterBase,
      );
      setResumo(normalizeResumo(response?.data));
    } catch {
      setResumo(EMPTY_RESUMO);
    } finally {
      setResumoLoading(false);
    }
  }, [apiFilterBase, reload]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  const onExport = () => {
    generateReportDigitadorDiario(localFilter);
  };

  const columns = [
    {
      field: 'dataDigitacao',
      headerName: 'Digitado em',
      minWidth: 120,
      flex: 0.9,
      renderCell: (params) => formatDate(params.row.dataDigitacao),
    },
    { field: 'digitadorNome', headerName: 'Digitador', minWidth: 140, flex: 1 },
    {
      field: 'tipoServicoSubtipo',
      headerName: 'Serviço',
      minWidth: 160,
      flex: 1,
      sortable: false,
      renderCell: (params) => renderTipoServicoSubtipo(params.row),
    },
    { field: 'clienteNome', headerName: 'Cliente', minWidth: 120, flex: 1 },
    {
      field: 'nsirStatus',
      headerName: 'NSIR',
      minWidth: 200,
      flex: 1.1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => renderNsirStatus(params.row),
    },
    {
      field: 'malhaGerencia',
      headerName: 'Malha / Gerência',
      minWidth: 160,
      flex: 1,
      sortable: false,
      renderCell: (params) => renderMalhaGerencia(params.row),
    },
    {
      field: 'localRede',
      headerName: 'Local',
      minWidth: 180,
      flex: 1.1,
      sortable: false,
      renderCell: (params) => renderLocalRede(params.row),
    },
    {
      field: 'volumesDigitacao',
      headerName: 'Volumes',
      minWidth: 140,
      flex: 0.9,
      sortable: false,
      renderCell: (params) => renderVolumes(params.row),
    },
    {
      field: 'ultimaDigitacao',
      headerName: 'Última Digitação',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => formatDateTime(params.row.ultimaDigitacao),
    },
  ];

  if (authData?.role === Role.DIGITADOR) {
    return <Redirect to="/v2/minha-digitacao" />;
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Relatório de Digitação</Typography>
            {periodoLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {periodoLabel}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FilterListOutlinedIcon />}
              onClick={() => setOpenFilter(true)}
            >
              Filtros
            </Button>
            {/* <Button
              variant="outlined"
              startIcon={<TableChartOutlinedIcon />}
              onClick={() => setOpenPivot(true)}
            >
              Explorar
            </Button> */}
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={onExport}
            >
              Exportar
            </Button>
          </Stack>
        </Stack>

        <KpiStrip
          resumo={resumo}
          loading={resumoLoading}
          activeStatus={activeStatus}
          onSelectStatus={setActiveStatus}
        />

        <ChartsRow apiFilterBase={apiFilterBase} refreshKey={reload} />

        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="h6">Detalhamento</Typography>
          </Stack>
          <TableV2
            useApi
            fetchUrl="reports/digitacao/filter"
            paginationUrl="/v2/relatorio-digitacao"
            filterData={apiFilter}
            refreshKey={reload}
            rowHeight={70}
            pageSize={10}
            columns={columns}
            emptyMessage="Nenhum registro encontrado"
          />
        </Box>

        <PivotExplorerDialog
          open={openPivot}
          onClose={() => setOpenPivot(false)}
          apiFilter={apiFilter}
          refreshKey={reload}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          confirmButtonText="Aplicar"
          fields={[
            { field: 'useDates', useDateTypes: true, useDateTypesReadOnly: true },
            {
              field: 'useDigitador',
              multiSelect: true,
              disabled: !isDigitadorAdmin,
              md: 6,
              xs: 12,
            },
            { field: 'useInspetor', multiSelect: true, md: 6, xs: 12 },
            { field: 'useCodigo', codigoLabel: 'Código NSIR', md: 4, xs: 12 },
            { field: 'useMalha', md: 4, xs: 12, multiSelect: true },
            { field: 'useGerencia', md: 4, xs: 12 },
            { field: 'usePolo', md: 4, xs: 12 },
            { field: 'useSubestacao', md: 4, xs: 12 },
            { field: 'useAlimentador', md: 4, xs: 12 },
            { field: 'useTipoServico', md: 6, xs: 12 },
            { field: 'useSubTipoServico', md: 6, xs: 12 },
            {
              field: 'useNsirStatus',
              customStatus: STATUS_FILTER_OPTIONS,
              multiSelect: true,
              md: 12,
              xs: 12,
            },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setActiveStatus(null);
            setOpenFilter(false);
            setReload((prev) => !prev);
          }}
        />
      </Stack>
    </Box>
  );
};

RelatorioDigitacaoV2.propTypes = {
  authData: PropTypes.object.isRequired,
  generateReportDigitadorDiario: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      generateReportDigitadorDiario: actionsNsir.generateReportDigitadorDiario,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(RelatorioDigitacaoV2);
